import type { PayloadAction } from "@reduxjs/toolkit"
import { createSelector, createSlice } from "@reduxjs/toolkit"

import { calendarApi, userApi } from "@/api"
import { getErrorResponse } from "@/api/errorUtils"
import type {
  AcceptInvitationResponse,
  BatchInviteRequest,
  BuySeatsRequest,
  BuySeatsResponse,
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  ReduceSeatsRequest,
  ResubscribeWorkspaceRequest,
  Workspace,
  WorkspaceAutoReloadSettings,
  WorkspaceAutoReloadSettingsRequest,
  WorkspaceCreditsSnapshot,
  WorkspaceInvitation,
  WorkspaceInvoice,
  WorkspaceMember,
  WorkspaceRole,
  WorkspaceScheduledChange,
  WorkspaceSummary,
} from "@/api/types"
import { workspaceApi } from "@/api/workspaceApi"
import type { CreditPackItem } from "@/components/credits/CreditsPackCards/CreditsPackCards"
import { logger } from "@/lib/logger"
import type { PlanBillingCycle } from "@/pages/LandingPage/sections/Plans/plansData"
import { ensureBillingSummary, ensureBillingUsage } from "@/store/billing/slice"
import { setUser, updateUser } from "@/store/currentUser/slice"
import { logoutAction } from "@/store/session/logoutAction"
import type { AppDispatch, RootState } from "@/store/store"
import { setUpcomingMeetings } from "@/store/upcoming/slice"

interface WorkspaceState {
  workspaces: WorkspaceSummary[]
  workspacesLastFetchedAt: number | null
  isFetchingWorkspaces: boolean

  activeWorkspaceId: string | null
  activeContextRevision: number
  activeWorkspace: Workspace | null
  activeWorkspaceLastFetchedAt: number | null
  isFetchingActiveWorkspace: boolean

  membersByWorkspaceId: Record<string, WorkspaceMember[]>
  membersLastFetchedAtByWorkspaceId: Record<string, number>
  isFetchingMembersByWorkspaceId: Record<string, boolean>

  workspaceCreditsLastFetchedAt: number | null
  isFetchingWorkspaceCredits: boolean

  scheduledChange: WorkspaceScheduledChange | null

  invoicesByWorkspaceId: Record<string, WorkspaceInvoice[]>
  invoicesLastFetchedAtByWorkspaceId: Record<string, number>
  isFetchingInvoicesByWorkspaceId: Record<string, boolean>

  autoReloadSettingsByWorkspaceId: Record<
    string,
    WorkspaceAutoReloadSettings | null
  >
  autoReloadSettingsLastFetchedAtByWorkspaceId: Record<string, number>
  isFetchingAutoReloadSettingsByWorkspaceId: Record<string, boolean>

  creditPacksByWorkspaceId: Record<string, CreditPackItem[]>
  creditPacksLastFetchedAtByWorkspaceId: Record<string, number>
  isFetchingCreditPacksByWorkspaceId: Record<string, boolean>
}

const initialState: WorkspaceState = {
  workspaces: [],
  workspacesLastFetchedAt: null,
  isFetchingWorkspaces: false,

  activeWorkspaceId: null,
  activeContextRevision: 0,
  activeWorkspace: null,
  activeWorkspaceLastFetchedAt: null,
  isFetchingActiveWorkspace: false,

  membersByWorkspaceId: {},
  membersLastFetchedAtByWorkspaceId: {},
  isFetchingMembersByWorkspaceId: {},

  workspaceCreditsLastFetchedAt: null,
  isFetchingWorkspaceCredits: false,

  scheduledChange: null,

  invoicesByWorkspaceId: {},
  invoicesLastFetchedAtByWorkspaceId: {},
  isFetchingInvoicesByWorkspaceId: {},

  autoReloadSettingsByWorkspaceId: {},
  autoReloadSettingsLastFetchedAtByWorkspaceId: {},
  isFetchingAutoReloadSettingsByWorkspaceId: {},

  creditPacksByWorkspaceId: {},
  creditPacksLastFetchedAtByWorkspaceId: {},
  isFetchingCreditPacksByWorkspaceId: {},
}

const WORKSPACES_STALE_AFTER_MS = 5 * 60 * 1000
const ACTIVE_WORKSPACE_STALE_AFTER_MS = 60 * 1000
const MEMBERS_STALE_AFTER_MS = 60 * 1000
const CREDITS_STALE_AFTER_MS = 15 * 1000
const INVOICES_STALE_AFTER_MS = 5 * 60 * 1000
const AUTO_RELOAD_STALE_AFTER_MS = 60 * 1000
const CREDIT_PACKS_STALE_AFTER_MS = 60 * 1000

// Invalidate same-workspace resets too: an A -> Personal -> A response must
// never be reused after the active context changes, regardless of screen size.
const invalidateActiveContextRequests = (state: WorkspaceState) => {
  state.activeContextRevision += 1
  state.isFetchingActiveWorkspace = false
  state.isFetchingWorkspaceCredits = false
  state.workspaceCreditsLastFetchedAt = null
}

const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    setWorkspaces: (state, action: PayloadAction<WorkspaceSummary[]>) => {
      state.workspaces = action.payload
      state.workspacesLastFetchedAt = Date.now()
    },
    setIsFetchingWorkspaces: (state, action: PayloadAction<boolean>) => {
      state.isFetchingWorkspaces = action.payload
    },

    setActiveWorkspace: (
      state,
      action: PayloadAction<{
        workspaceId: string | null
        workspace: Workspace | null
      }>,
    ) => {
      if (
        state.activeWorkspaceId !== action.payload.workspaceId ||
        !action.payload.workspace
      ) {
        invalidateActiveContextRequests(state)
      }
      state.activeWorkspaceId = action.payload.workspaceId
      state.activeWorkspace = action.payload.workspace
      state.activeWorkspaceLastFetchedAt = Date.now()
      if (action.payload.workspace?.scheduledChange) {
        state.scheduledChange = action.payload.workspace.scheduledChange
      } else {
        state.scheduledChange = null
      }
    },
    setIsFetchingActiveWorkspace: (state, action: PayloadAction<boolean>) => {
      state.isFetchingActiveWorkspace = action.payload
    },

    setMembers: (
      state,
      action: PayloadAction<{
        workspaceId: string
        members: WorkspaceMember[]
      }>,
    ) => {
      state.membersByWorkspaceId[action.payload.workspaceId] =
        action.payload.members
      state.membersLastFetchedAtByWorkspaceId[action.payload.workspaceId] =
        Date.now()
    },
    setIsFetchingMembers: (
      state,
      action: PayloadAction<{ workspaceId: string; value: boolean }>,
    ) => {
      state.isFetchingMembersByWorkspaceId[action.payload.workspaceId] =
        action.payload.value
    },

    setWorkspaceCredits: {
      reducer: (
        state,
        action: PayloadAction<
          WorkspaceCreditsSnapshot | null,
          string,
          { workspaceId?: string; activeContextRevision?: number }
        >,
      ) => {
        if (
          action.meta.workspaceId !== undefined &&
          (action.meta.workspaceId !== state.activeWorkspaceId ||
            action.meta.workspaceId !== state.activeWorkspace?.id ||
            action.meta.activeContextRevision !== state.activeContextRevision)
        ) {
          return
        }
        if (state.activeWorkspace && action.payload) {
          state.activeWorkspace.credits = action.payload
        }
        state.workspaceCreditsLastFetchedAt = Date.now()
      },
      prepare: (
        credits: WorkspaceCreditsSnapshot | null,
        workspaceId?: string,
        activeContextRevision?: number,
      ) => ({
        payload: credits,
        meta: { workspaceId, activeContextRevision },
      }),
    },
    setIsFetchingWorkspaceCredits: (state, action: PayloadAction<boolean>) => {
      state.isFetchingWorkspaceCredits = action.payload
    },

    setScheduledChange: (
      state,
      action: PayloadAction<WorkspaceScheduledChange | null>,
    ) => {
      state.scheduledChange = action.payload
    },

    setInvoices: (
      state,
      action: PayloadAction<{
        workspaceId: string
        invoices: WorkspaceInvoice[]
      }>,
    ) => {
      state.invoicesByWorkspaceId[action.payload.workspaceId] =
        action.payload.invoices
      state.invoicesLastFetchedAtByWorkspaceId[action.payload.workspaceId] =
        Date.now()
    },
    setIsFetchingInvoices: (
      state,
      action: PayloadAction<{ workspaceId: string; value: boolean }>,
    ) => {
      state.isFetchingInvoicesByWorkspaceId[action.payload.workspaceId] =
        action.payload.value
    },

    setAutoReloadSettings: (
      state,
      action: PayloadAction<{
        workspaceId: string
        settings: WorkspaceAutoReloadSettings | null
      }>,
    ) => {
      state.autoReloadSettingsByWorkspaceId[action.payload.workspaceId] =
        action.payload.settings
      state.autoReloadSettingsLastFetchedAtByWorkspaceId[
        action.payload.workspaceId
      ] = Date.now()
    },
    setIsFetchingAutoReloadSettings: (
      state,
      action: PayloadAction<{ workspaceId: string; value: boolean }>,
    ) => {
      state.isFetchingAutoReloadSettingsByWorkspaceId[
        action.payload.workspaceId
      ] = action.payload.value
    },

    setCreditPacks: (
      state,
      action: PayloadAction<{ workspaceId: string; packs: CreditPackItem[] }>,
    ) => {
      state.creditPacksByWorkspaceId[action.payload.workspaceId] =
        action.payload.packs
      state.creditPacksLastFetchedAtByWorkspaceId[action.payload.workspaceId] =
        Date.now()
    },
    setIsFetchingCreditPacks: (
      state,
      action: PayloadAction<{ workspaceId: string; value: boolean }>,
    ) => {
      state.isFetchingCreditPacksByWorkspaceId[action.payload.workspaceId] =
        action.payload.value
    },

    updateWorkspaceSummaryOptimistic: (
      state,
      action: PayloadAction<{
        workspaceId: string
        name?: string
        avatar?: string
        deletionAt?: number | null
        deletionReason?: "owner_requested" | "payment_failed" | null
      }>,
    ) => {
      const { workspaceId, name, avatar, deletionAt, deletionReason } =
        action.payload
      const summary = state.workspaces.find((s) => s.id === workspaceId)
      if (summary) {
        if (name !== undefined) summary.name = name
        if (avatar !== undefined) summary.avatar = avatar
      }
      if (state.activeWorkspace && state.activeWorkspace.id === workspaceId) {
        if (name !== undefined) state.activeWorkspace.name = name
        if (avatar !== undefined) state.activeWorkspace.avatar = avatar
        if (deletionAt !== undefined)
          state.activeWorkspace.deletionAt = deletionAt ?? undefined
        if (deletionReason !== undefined)
          state.activeWorkspace.deletionReason = deletionReason ?? undefined
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, (state) => ({
      ...initialState,
      activeContextRevision: state.activeContextRevision + 1,
    }))
    builder.addCase(updateUser, (state, action) => {
      if (action.payload.workspaces) {
        state.workspaces = action.payload.workspaces
        state.workspacesLastFetchedAt = Date.now()
      }
      if (action.payload.activeContext !== undefined) {
        const workspaceId =
          action.payload.activeContext === "personal"
            ? null
            : (action.payload.activeContext ?? null)
        if (state.activeWorkspaceId !== workspaceId) {
          invalidateActiveContextRequests(state)
        }
        state.activeWorkspaceId = workspaceId
      }
    })
    builder.addCase(setUser, (state, action) => {
      if (action.payload?.workspaces) {
        state.workspaces = action.payload.workspaces
        state.workspacesLastFetchedAt = Date.now()
      }
      if (action.payload?.activeContext !== undefined) {
        const workspaceId =
          action.payload.activeContext === "personal"
            ? null
            : (action.payload.activeContext ?? null)
        if (state.activeWorkspaceId !== workspaceId) {
          invalidateActiveContextRequests(state)
        }
        state.activeWorkspaceId = workspaceId
      }
    })
  },
})

export const {
  setWorkspaces,
  setIsFetchingWorkspaces,
  setActiveWorkspace,
  setIsFetchingActiveWorkspace,
  setMembers,
  setIsFetchingMembers,
  setWorkspaceCredits,
  setIsFetchingWorkspaceCredits,
  setScheduledChange,
  setInvoices,
  setIsFetchingInvoices,
  setAutoReloadSettings,
  setIsFetchingAutoReloadSettings,
  setCreditPacks,
  setIsFetchingCreditPacks,
  updateWorkspaceSummaryOptimistic,
} = workspaceSlice.actions

export const selectWorkspaces = (state: RootState) => state.workspace.workspaces
export const selectIsFetchingWorkspaces = (state: RootState) =>
  state.workspace.isFetchingWorkspaces
export const selectActiveWorkspaceId = (state: RootState) =>
  state.workspace.activeWorkspaceId
export const selectActiveWorkspace = (state: RootState) =>
  state.workspace.activeWorkspace
export const selectActiveWorkspaceMembers = (state: RootState) => {
  const id = state.workspace.activeWorkspaceId
  return id ? (state.workspace.membersByWorkspaceId[id] ?? []) : []
}
export const selectActiveWorkspaceCredits = (state: RootState) =>
  state.workspace.activeWorkspace?.credits
export const selectActiveWorkspaceScheduledChange = (state: RootState) =>
  state.workspace.scheduledChange
export const selectActiveWorkspaceInvoices = (state: RootState) => {
  const id = state.workspace.activeWorkspaceId
  return id ? (state.workspace.invoicesByWorkspaceId[id] ?? []) : []
}
export const selectAutoReloadSettings = (state: RootState) => {
  const id = state.workspace.activeWorkspaceId
  return id
    ? (state.workspace.autoReloadSettingsByWorkspaceId[id] ?? null)
    : null
}
export const selectCreditPacks = (state: RootState) => {
  const id = state.workspace.activeWorkspaceId
  return id ? (state.workspace.creditPacksByWorkspaceId[id] ?? []) : []
}

export const selectWorkspacePermissions = createSelector(
  selectActiveWorkspace,
  (ws) => ({
    canSeePrice: ws?.role === "owner" || ws?.role === "admin",
    canDeleteWorkspace: ws?.role === "owner",
    canChangePlan: ws?.role === "owner",
    canBuySeats: ws?.role === "owner",
    canEditWorkspace: ws?.role === "owner" || ws?.role === "admin",
    canViewBilling: ws?.role === "owner" || ws?.role === "admin",
    canManageBilling: ws?.role === "owner",
    canBuyCredits: ws?.role === "owner",
    canManageAutoReload: ws?.role === "owner",
    canManageMembers: ws?.role === "owner" || ws?.role === "admin",
    canInviteMembers: ws?.role === "owner" || ws?.role === "admin",
    canRemoveMembers: ws?.role === "owner" || ws?.role === "admin",
    canChangeRoles: ws?.role === "owner" || ws?.role === "admin",
    canLeaveWorkspace: ws?.role === "admin" || ws?.role === "member",
    canViewCredits: ws?.role === "owner",
    canViewStorage:
      ws?.role === "owner" || ws?.role === "admin" || ws?.role === "member",
    canViewInvoices: ws?.role === "owner",
    canResubscribeWorkspace: ws?.role === "owner",
  }),
)

export type ActiveContext =
  | { type: "personal" }
  | {
      type: "workspace"
      workspaceId: string
      workspaceName: string
      role: WorkspaceRole
    }

export const selectActiveContext = createSelector(
  [selectActiveWorkspaceId, selectWorkspaces, selectActiveWorkspace],
  (activeId, workspaces, activeWs): ActiveContext => {
    if (!activeId) return { type: "personal" }
    const ws = workspaces.find((w) => w.id === activeId)
    return {
      type: "workspace",
      workspaceId: activeId,
      workspaceName: activeWs?.name ?? ws?.name ?? "",
      role: activeWs?.role ?? ws?.role ?? "member",
    }
  },
)

const isStale = (
  lastFetchedAt: number | null | undefined,
  ttlMs: number,
): boolean => {
  if (!lastFetchedAt) return true
  return Date.now() - lastFetchedAt > ttlMs
}

interface FetchOptions {
  force?: boolean
}

let workspacesRequest: Promise<WorkspaceSummary[]> | null = null
const activeWorkspaceRequests = new Map<string, Promise<Workspace>>()
const workspaceCreditsRequests = new Map<
  string,
  Promise<WorkspaceCreditsSnapshot>
>()

const getContextRequestKey = (workspaceId: string, revision: number) =>
  `${revision}:${workspaceId}`

const hasCurrentContextRequest = (
  requests: ReadonlyMap<string, unknown>,
  state: WorkspaceState,
) =>
  state.activeWorkspaceId !== null &&
  requests.has(
    getContextRequestKey(state.activeWorkspaceId, state.activeContextRevision),
  )

const membersRequests: Record<string, Promise<WorkspaceMember[]> | null> = {}
const invoicesRequests: Record<string, Promise<WorkspaceInvoice[]> | null> = {}
const autoReloadSettingsRequests: Record<
  string,
  Promise<WorkspaceAutoReloadSettings | null> | null
> = {}
const creditPacksRequests: Record<string, Promise<CreditPackItem[]> | null> = {}

export const ensureWorkspaces =
  (options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<WorkspaceSummary[]> => {
    const state = getState().workspace

    if (
      !options.force &&
      !isStale(state.workspacesLastFetchedAt, WORKSPACES_STALE_AFTER_MS) &&
      state.workspaces.length
    ) {
      return state.workspaces
    }

    if (state.isFetchingWorkspaces && workspacesRequest) {
      return workspacesRequest
    }

    dispatch(setIsFetchingWorkspaces(true))

    workspacesRequest = (async () => {
      try {
        const workspaces = await workspaceApi.getMyWorkspaces()
        dispatch(setWorkspaces(workspaces))
        return workspaces
      } catch (error) {
        logger.error("[workspaceSlice] Failed to fetch workspaces", error)
        throw error
      } finally {
        dispatch(setIsFetchingWorkspaces(false))
        workspacesRequest = null
      }
    })()

    return workspacesRequest
  }

export const ensureActiveWorkspace =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<Workspace> => {
    const state = getState().workspace
    const revision = state.activeContextRevision
    const requestKey = getContextRequestKey(workspaceId, revision)

    if (
      !options.force &&
      state.activeWorkspaceId === workspaceId &&
      !isStale(
        state.activeWorkspaceLastFetchedAt,
        ACTIVE_WORKSPACE_STALE_AFTER_MS,
      ) &&
      state.activeWorkspace &&
      state.activeWorkspace.id === workspaceId
    ) {
      return state.activeWorkspace
    }

    const existingRequest = activeWorkspaceRequests.get(requestKey)
    if (existingRequest) {
      return existingRequest
    }

    const request = (async () => {
      try {
        const workspace = await workspaceApi.getWorkspace(workspaceId)
        const current = getState().workspace
        if (
          current.activeWorkspaceId === workspaceId &&
          current.activeContextRevision === revision
        ) {
          dispatch(setActiveWorkspace({ workspaceId, workspace }))
        }
        return workspace
      } catch (error) {
        logger.error("[workspaceSlice] Failed to fetch active workspace", error)
        throw error
      } finally {
        activeWorkspaceRequests.delete(requestKey)
        dispatch(
          setIsFetchingActiveWorkspace(
            hasCurrentContextRequest(
              activeWorkspaceRequests,
              getState().workspace,
            ),
          ),
        )
      }
    })()

    activeWorkspaceRequests.set(requestKey, request)
    if (state.activeWorkspaceId === workspaceId) {
      dispatch(setIsFetchingActiveWorkspace(true))
    }
    return request
  }

export const ensureMembers =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<WorkspaceMember[]> => {
    const state = getState().workspace
    const cached = state.membersByWorkspaceId[workspaceId]
    const lastFetched = state.membersLastFetchedAtByWorkspaceId[workspaceId]

    if (
      !options.force &&
      !isStale(lastFetched, MEMBERS_STALE_AFTER_MS) &&
      cached?.length
    ) {
      return cached
    }

    if (membersRequests[workspaceId]) {
      return membersRequests[workspaceId]!
    }

    dispatch(setIsFetchingMembers({ workspaceId, value: true }))

    membersRequests[workspaceId] = (async () => {
      try {
        const members = await workspaceApi.getMembers(workspaceId)
        dispatch(setMembers({ workspaceId, members }))
        return members
      } catch (error) {
        logger.error("[workspaceSlice] Failed to fetch members", error)
        throw error
      } finally {
        dispatch(setIsFetchingMembers({ workspaceId, value: false }))
        membersRequests[workspaceId] = null
      }
    })()

    return membersRequests[workspaceId]!
  }

export const ensureWorkspaceCredits =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<WorkspaceCreditsSnapshot> => {
    const state = getState().workspace
    const revision = state.activeContextRevision
    const requestKey = getContextRequestKey(workspaceId, revision)

    if (
      !options.force &&
      state.activeWorkspaceId === workspaceId &&
      state.activeWorkspace?.id === workspaceId &&
      !isStale(state.workspaceCreditsLastFetchedAt, CREDITS_STALE_AFTER_MS) &&
      state.activeWorkspace?.credits
    ) {
      return state.activeWorkspace?.credits
    }

    const existingRequest = workspaceCreditsRequests.get(requestKey)
    if (existingRequest) {
      return existingRequest
    }

    const request = (async () => {
      try {
        const credits = await workspaceApi.checkWorkspaceCredits(workspaceId)
        dispatch(setWorkspaceCredits(credits, workspaceId, revision))
        return credits
      } catch (error) {
        logger.error(
          "[workspaceSlice] Failed to fetch workspace credits",
          error,
        )
        throw error
      } finally {
        workspaceCreditsRequests.delete(requestKey)
        dispatch(
          setIsFetchingWorkspaceCredits(
            hasCurrentContextRequest(
              workspaceCreditsRequests,
              getState().workspace,
            ),
          ),
        )
      }
    })()

    workspaceCreditsRequests.set(requestKey, request)
    if (state.activeWorkspaceId === workspaceId) {
      dispatch(setIsFetchingWorkspaceCredits(true))
    }
    return request
  }

export const ensureInvoices =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<WorkspaceInvoice[]> => {
    const state = getState().workspace
    const cached = state.invoicesByWorkspaceId[workspaceId]
    const lastFetched = state.invoicesLastFetchedAtByWorkspaceId[workspaceId]

    if (
      !options.force &&
      !isStale(lastFetched, INVOICES_STALE_AFTER_MS) &&
      cached?.length
    ) {
      return cached
    }

    if (invoicesRequests[workspaceId]) {
      return invoicesRequests[workspaceId]!
    }

    dispatch(setIsFetchingInvoices({ workspaceId, value: true }))

    invoicesRequests[workspaceId] = (async () => {
      try {
        const invoices = await workspaceApi.getWorkspaceInvoices(workspaceId)
        dispatch(setInvoices({ workspaceId, invoices }))
        return invoices
      } catch (error) {
        logger.error("[workspaceSlice] Failed to fetch invoices", error)
        throw error
      } finally {
        dispatch(setIsFetchingInvoices({ workspaceId, value: false }))
        invoicesRequests[workspaceId] = null
      }
    })()

    return invoicesRequests[workspaceId]!
  }

export const ensureAutoReloadSettings =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<WorkspaceAutoReloadSettings | null> => {
    const state = getState().workspace
    const cached = state.autoReloadSettingsByWorkspaceId[workspaceId]
    const lastFetched =
      state.autoReloadSettingsLastFetchedAtByWorkspaceId[workspaceId]

    if (!options.force && !isStale(lastFetched, AUTO_RELOAD_STALE_AFTER_MS)) {
      return cached ?? null
    }

    if (autoReloadSettingsRequests[workspaceId]) {
      return autoReloadSettingsRequests[workspaceId]!
    }

    dispatch(setIsFetchingAutoReloadSettings({ workspaceId, value: true }))

    autoReloadSettingsRequests[workspaceId] = (async () => {
      try {
        const settings = await workspaceApi.getAutoReloadSettings(workspaceId)
        dispatch(setAutoReloadSettings({ workspaceId, settings }))
        return settings
      } catch (error) {
        logger.error(
          "[workspaceSlice] Failed to fetch auto-reload settings",
          error,
        )
        throw error
      } finally {
        dispatch(setIsFetchingAutoReloadSettings({ workspaceId, value: false }))
        autoReloadSettingsRequests[workspaceId] = null
      }
    })()

    return autoReloadSettingsRequests[workspaceId]!
  }

export const ensureCreditPacks =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<CreditPackItem[]> => {
    const state = getState().workspace
    const cached = state.creditPacksByWorkspaceId[workspaceId]
    const lastFetched = state.creditPacksLastFetchedAtByWorkspaceId[workspaceId]

    if (
      !options.force &&
      !isStale(lastFetched, CREDIT_PACKS_STALE_AFTER_MS) &&
      cached?.length
    ) {
      return cached
    }

    if (creditPacksRequests[workspaceId]) {
      return creditPacksRequests[workspaceId]!
    }

    dispatch(setIsFetchingCreditPacks({ workspaceId, value: true }))

    creditPacksRequests[workspaceId] = (async () => {
      try {
        const packs = await workspaceApi.getCreditsPackPricing(workspaceId)
        dispatch(setCreditPacks({ workspaceId, packs }))
        return packs
      } catch (error) {
        logger.error("[workspaceSlice] Failed to fetch credit packs", error)
        throw error
      } finally {
        dispatch(setIsFetchingCreditPacks({ workspaceId, value: false }))
        creditPacksRequests[workspaceId] = null
      }
    })()

    return creditPacksRequests[workspaceId]!
  }

export const removeMember =
  (workspaceId: string, userId: string) => async (dispatch: AppDispatch) => {
    try {
      await workspaceApi.removeMember(workspaceId, userId)
    } finally {
      dispatch(ensureMembers(workspaceId, { force: true }))
      dispatch(ensureActiveWorkspace(workspaceId, { force: true }))
    }
  }

export const changeMemberRole =
  (workspaceId: string, userId: string, role: WorkspaceRole) =>
  async (dispatch: AppDispatch) => {
    try {
      await workspaceApi.changeMemberRole(workspaceId, userId, role)
    } finally {
      dispatch(ensureMembers(workspaceId, { force: true }))
    }
  }

export const revokeInvitation =
  (workspaceId: string, invitationId: string) =>
  async (dispatch: AppDispatch) => {
    try {
      await workspaceApi.revokeInvitation(workspaceId, invitationId)
      dispatch(ensureMembers(workspaceId, { force: true }))
      dispatch(ensureActiveWorkspace(workspaceId, { force: true }))
    } catch {
      throw new Error("Failed to revoke invitation")
    }
  }

export const removeWorkspaceMemberRow =
  (workspaceId: string, member: WorkspaceMember) =>
  async (dispatch: AppDispatch) => {
    if (member.role === "owner") {
      throw new Error("Cannot remove owner")
    }
    if (member.status === "pending" || member.status === "declined") {
      if (!member.invitationId) {
        throw new Error("Invitation member missing invitationId")
      }
      await dispatch(revokeInvitation(workspaceId, member.invitationId))
    } else {
      if (!member.userId) {
        throw new Error("Active member missing userId")
      }
      await dispatch(removeMember(workspaceId, member.userId))
    }
  }

export const inviteMembersThunk =
  (req: BatchInviteRequest) =>
  async (
    dispatch: AppDispatch,
  ): Promise<{ blockedBy: string[]; alreadyInvited: string[] }> => {
    const result = await workspaceApi.inviteByEmailBatch(req)

    dispatch(ensureMembers(req.workspaceId, { force: true }))
    dispatch(ensureActiveWorkspace(req.workspaceId, { force: true }))

    return result
  }

export const fetchInvitationById =
  (invitationId: string) => async (): Promise<WorkspaceInvitation> => {
    return await workspaceApi.getInvitationById(invitationId)
  }

export const acceptInvitationThunk =
  (invitationId: string) => async (): Promise<AcceptInvitationResponse> => {
    return await workspaceApi.acceptInvitation(invitationId)
  }

export const declineInvitationThunk =
  (invitationId: string) => async (): Promise<void> => {
    await workspaceApi.declineInvitation(invitationId)
  }

export const scheduleReduceSeatsThunk =
  (req: ReduceSeatsRequest) => async (dispatch: AppDispatch) => {
    const change = await workspaceApi.scheduleReduceSeats(req)
    dispatch(setScheduledChange(change))
    return change
  }

export const cancelScheduledChangeThunk =
  (workspaceId: string) => async (dispatch: AppDispatch) => {
    await workspaceApi.cancelScheduledChange(workspaceId)
    dispatch(setScheduledChange(null))
    dispatch(ensureActiveWorkspace(workspaceId, { force: true }))
  }

export const cancelWorkspacePlanThunk =
  (workspaceId: string) => async (dispatch: AppDispatch) => {
    const workspace = await workspaceApi.cancelWorkspacePlan(workspaceId)
    dispatch(
      setActiveWorkspace({
        workspaceId,
        workspace: { ...workspace, cancelAtPeriodEnd: true },
      }),
    )
  }

export const reactivateWorkspacePlanThunk =
  (workspaceId: string) => async (dispatch: AppDispatch) => {
    const workspace = await workspaceApi.reactivateWorkspacePlan(workspaceId)
    dispatch(
      setActiveWorkspace({
        workspaceId,
        workspace: { ...workspace, cancelAtPeriodEnd: false },
      }),
    )
  }

export const changeWorkspacePlanIntervalThunk =
  (workspaceId: string, interval: PlanBillingCycle) =>
  async (dispatch: AppDispatch) => {
    await workspaceApi.changeWorkspacePlanInterval(workspaceId, interval)
    dispatch(ensureActiveWorkspace(workspaceId, { force: true }))
  }

export const createWorkspaceThunk =
  (req: CreateWorkspaceRequest) =>
  async (): Promise<CreateWorkspaceResponse> => {
    const response = await workspaceApi.createWorkspace(req)
    sessionStorage.setItem("pendingWorkspaceId", response.workspaceId)
    sessionStorage.setItem("pendingWorkspaceCheckoutType", "create")
    return response
  }

export const buySeatsThunk =
  (req: BuySeatsRequest) =>
  async (dispatch: AppDispatch): Promise<BuySeatsResponse> => {
    const workspace = await workspaceApi.buySeats(req)
    dispatch(setActiveWorkspace({ workspaceId: req.workspaceId, workspace }))
    return workspace
  }

export const buyCreditsPackThunk =
  (
    workspaceId: string,
    packSize: number,
    successPath: string,
    cancelPath: string,
  ) =>
  async (): Promise<{ url: string }> => {
    return await workspaceApi.buyCreditsPack(
      workspaceId,
      packSize,
      successPath,
      cancelPath,
    )
  }

export const saveAutoReloadSettingsThunk =
  (workspaceId: string, settings: WorkspaceAutoReloadSettingsRequest) =>
  async (dispatch: AppDispatch) => {
    const saved = await workspaceApi.saveAutoReloadSettings(
      workspaceId,
      settings,
    )
    dispatch(setAutoReloadSettings({ workspaceId, settings: saved }))
  }

export const deleteAutoReloadSettingsThunk =
  (workspaceId: string) => async (dispatch: AppDispatch) => {
    await workspaceApi.deleteAutoReloadSettings(workspaceId)
    dispatch(setAutoReloadSettings({ workspaceId, settings: null }))
  }

export const resubscribeWorkspaceThunk =
  (req: ResubscribeWorkspaceRequest) =>
  async (): Promise<CreateWorkspaceResponse> => {
    return await workspaceApi.resubscribeWorkspace(req)
  }

export class ContextSwitchError extends Error {
  code: string
  constructor(code: string) {
    super(code)
    this.code = code
    this.name = "ContextSwitchError"
  }
}

const extractErrorCode = (error: unknown): string | undefined => {
  const response = getErrorResponse(error)
  if (!response?.data) return undefined
  if (typeof response.data === "string") return response.data
  if (typeof response.data === "object" && response.data !== null) {
    const data = response.data as { error?: unknown }
    return typeof data.error === "string" ? data.error : undefined
  }
  return undefined
}

export const setActiveContext =
  (workspaceId: string | null) => async (dispatch: AppDispatch) => {
    const context = workspaceId ?? "personal"

    try {
      const currentUser = await userApi.setActiveContext(context)
      dispatch(
        updateUser({
          activeContext: currentUser.activeContext,
          workspaces: currentUser.workspaces,
          ...(currentUser.billingPlanId !== undefined
            ? { billingPlanId: currentUser.billingPlanId }
            : {}),
        }),
      )
    } catch (error) {
      const errorCode = extractErrorCode(error)

      if (errorCode) {
        throw new ContextSwitchError(errorCode)
      }

      logger.error("[workspaceSlice] Failed to set active context", error)
      throw error
    }

    dispatch(setActiveWorkspace({ workspaceId, workspace: null }))

    dispatch(setUpcomingMeetings([]))
    try {
      const upcomingMeetings = await calendarApi.getUpcomingMeetings({
        count: 30,
      })
      dispatch(setUpcomingMeetings(upcomingMeetings))
    } catch (error) {
      logger.error(
        "[workspaceSlice] Failed to fetch upcoming meetings after context switch",
        error,
      )
    }

    if (workspaceId) {
      await dispatch(ensureActiveWorkspace(workspaceId, { force: true }))
    } else {
      void dispatch(ensureBillingSummary({ force: true }))
      void dispatch(ensureBillingUsage({ force: true }))
    }
  }

export const updateWorkspaceDetailsThunk =
  (req: { workspaceId: string; name?: string; avatar?: string }) =>
  async (dispatch: AppDispatch): Promise<Workspace> => {
    dispatch(updateWorkspaceSummaryOptimistic(req))
    try {
      return await workspaceApi.updateWorkspaceDetails(req)
    } catch (error) {
      await dispatch(ensureWorkspaces({ force: true }))
      throw error
    }
  }

export const uploadWorkspaceAvatarThunk =
  (workspaceId: string, file: File | Blob) =>
  async (dispatch: AppDispatch): Promise<{ file: string }> => {
    const response = await workspaceApi.uploadWorkspaceAvatar(workspaceId, file)
    dispatch(
      updateWorkspaceSummaryOptimistic({
        workspaceId,
        avatar: response.file,
      }),
    )
    return response
  }

export const scheduleWorkspaceDeletionThunk =
  (workspaceId: string) => async (dispatch: AppDispatch) => {
    await workspaceApi.deleteWorkspace(workspaceId)
    await dispatch(ensureWorkspaces({ force: true }))
    await dispatch(ensureActiveWorkspace(workspaceId, { force: true }))
  }

export const cancelWorkspaceDeletionThunk =
  (workspaceId: string) => async (dispatch: AppDispatch) => {
    const workspace = await workspaceApi.reactivateWorkspacePlan(workspaceId)
    dispatch(setActiveWorkspace({ workspaceId, workspace }))
    dispatch(ensureWorkspaces({ force: true }))
  }

export const leaveWorkspaceThunk =
  (workspaceId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const wasActive = getState().workspace.activeWorkspaceId === workspaceId
    await workspaceApi.leaveWorkspace(workspaceId)
    const remaining = getState().workspace.workspaces.filter(
      (s) => s.id !== workspaceId,
    )
    dispatch(setWorkspaces(remaining))
    if (wasActive) {
      dispatch(setActiveWorkspace({ workspaceId: null, workspace: null }))
    }
  }

export const deleteWorkspaceThunk =
  (workspaceId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const wasActive = getState().workspace.activeWorkspaceId === workspaceId
    await workspaceApi.deleteWorkspace(workspaceId)
    await dispatch(ensureWorkspaces({ force: true }))
    if (wasActive) {
      dispatch(setActiveWorkspace({ workspaceId: null, workspace: null }))
    }
  }

export default workspaceSlice.reducer
